/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import {
  IAffiliateFinancialDetail,
  IAffiliateSale,
  IPayoutRequest
} from "../../interfaces/endpoints/IAffiliates";
import { getAffiliateFinancialDetail } from "../../services/Affiliates.service";
import Modal from "../ui/Modal";
import Table, { Row } from "../ui/table";
import {
  AffiliateStatusBadge,
  PayoutStatusBadge
} from "./AffiliateStatusBadge";

interface IProps {
  affiliateId: number | null;
  onClose: () => void;
}

const formatEuro = (value: number) => `€${(value || 0).toFixed(2)}`;

const formatPaymentStatus = (status: number) => {
  switch (status) {
    case 1:
      return "Konfirmuar";
    case 0:
      return "Ne pritje";
    default:
      return "I panjohur";
  }
};

const formatReservationType = (type: string) => {
  switch (type) {
    case "JourneyReservation":
      return "Bileta autobusi";
    case "EsimReservation":
      return "eSIM";
    case "TravelBundle":
      return "Bundle";
    default:
      return type;
  }
};

const SummaryCard: FC<{ label: string; value: string; accent?: string }> = ({
  label,
  value,
  accent
}) => (
  <div className="border rounded-lg p-3 bg-white">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-base font-semibold ${accent || ""}`}>{value}</div>
  </div>
);

const salesColumns = [
  "ID",
  "Lloji",
  "Rezervimi",
  "Fitimi",
  "Zbritja",
  "Statusi i pageses",
  "Data"
];

const payoutsColumns = [
  "ID",
  "Shuma e kerkuar",
  "Shuma e aprovuar",
  "Statusi",
  "Banka",
  "IBAN",
  "Data e kerkeses",
  "Data e procesimit",
  "Shenim partneri",
  "Shenim admini"
];

const AffiliateFinancialDetailsModal: FC<IProps> = ({
  affiliateId,
  onClose
}) => {
  const [detail, setDetail] = useState<IAffiliateFinancialDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"sales" | "payouts">("sales");

  useEffect(() => {
    if (affiliateId === null) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setTab("sales");
    getAffiliateFinancialDetail(affiliateId)
      .then((res) => setDetail(res.data))
      .finally(() => setLoading(false));
  }, [affiliateId]);

  return (
    <Modal
      show={affiliateId !== null}
      onClose={onClose}
      title={
        detail
          ? `Pasqyra financiare - ${detail.affiliateName}`
          : "Pasqyra financiare"
      }
      position="center"
      modalWidth="900px"
      modalHeight="85vh"
    >
      <div className="p-4 space-y-4">
        {loading && <p className="text-sm text-gray-500">Duke u ngarkuar...</p>}
        {detail && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <div className="text-sm text-gray-500">Email</div>
                <div className="font-medium">{detail.email}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Kodi promo</div>
                <div className="font-medium">{detail.promoCode || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Statusi</div>
                <AffiliateStatusBadge status={detail.status} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard
                label="Numri i shitjeve"
                value={
                  `${detail.totalSalesCount} ` +
                  `(${detail.busSalesCount} Bus / ${detail.esimSalesCount} eSIM)`
                }
              />
              <SummaryCard
                label="Fitimet totale"
                value={formatEuro(detail.totalEarnings)}
              />
              <SummaryCard
                label="Fitimet ne pritje"
                value={formatEuro(detail.pendingEarnings)}
              />
              <SummaryCard
                label="Paguar totale"
                value={formatEuro(detail.totalPaidOut)}
              />
              <SummaryCard
                label="Pagesa ne pritje"
                value={formatEuro(detail.pendingPayoutAmount)}
              />
              <SummaryCard
                label="Numri i kerkesave per pagese"
                value={`${detail.payoutRequestsCount}`}
              />
              <SummaryCard
                label="Bilanci i disponueshem"
                value={formatEuro(detail.availableBalance)}
                accent={
                  detail.availableBalance > 0
                    ? "text-green-600"
                    : "text-gray-700"
                }
              />
              <SummaryCard
                label="Banka"
                value={detail.bankName || "-"}
              />
            </div>

            {(detail.iban || detail.accountHolderName) && (
              <div className="text-sm text-gray-600 border rounded-lg p-3 bg-gray-50">
                <div>
                  Mbajtesi i llogarise:{" "}
                  <strong>{detail.accountHolderName || "-"}</strong>
                </div>
                <div>
                  IBAN: <strong>{detail.iban || "-"}</strong>
                </div>
              </div>
            )}

            <div className="flex border-b">
              <button
                type="button"
                onClick={() => setTab("sales")}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                  tab === "sales"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500"
                }`}
              >
                Shitjet ({detail.sales.length})
              </button>
              <button
                type="button"
                onClick={() => setTab("payouts")}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                  tab === "payouts"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500"
                }`}
              >
                Kerkesat per pagese ({detail.payoutRequests.length})
              </button>
            </div>

            {tab === "sales" ? (
              <Table
                titles={salesColumns}
                className="overflow-x-auto"
                style="hover"
              >
                {detail.sales.map((s: IAffiliateSale, i: number) => (
                  <Row
                    key={i}
                    id={s.id}
                    type={formatReservationType(s.reservationType)}
                    reservationId={s.reservationId}
                    amountEarned={formatEuro(s.amountEarned)}
                    discountGiven={formatEuro(s.discountGiven)}
                    paymentStatus={formatPaymentStatus(s.paymentStatus)}
                    date={dayjs(s.insertDate).format("DD-MM-YYYY HH:mm")}
                  />
                ))}
              </Table>
            ) : (
              <Table
                titles={payoutsColumns}
                className="overflow-x-auto"
                style="hover"
              >
                {detail.payoutRequests.map((p: IPayoutRequest, i: number) => (
                  <Row
                    key={i}
                    id={p.id}
                    requestedAmount={formatEuro(p.requestedAmount)}
                    approvedAmount={
                      p.approvedAmount > 0 ? formatEuro(p.approvedAmount) : "-"
                    }
                    status={<PayoutStatusBadge status={p.status} />}
                    bankName={p.bankName || "-"}
                    iban={p.iban || "-"}
                    insertDate={dayjs(p.insertDate).format("DD-MM-YYYY")}
                    processedDate={
                      p.processedDate
                        ? dayjs(p.processedDate).format("DD-MM-YYYY")
                        : "-"
                    }
                    note={p.note || "-"}
                    adminNote={p.adminNote || "-"}
                  />
                ))}
              </Table>
            )}

            {tab === "sales" && detail.sales.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">
                Nuk ka shitje per kete partner.
              </p>
            )}
            {tab === "payouts" && detail.payoutRequests.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">
                Nuk ka kerkesa per pagese per kete partner.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AffiliateFinancialDetailsModal;
